import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { Box } from "src/components/Box";
import { Text } from "src/components/Text";
import { Radio, RadioGroup } from "src/components/Radio";
import { useTranslation } from "src/hooks";
import styled, { css } from "styled-components";
import Tag from "@asphalt-react/tag";
import { MediaQueries } from "src/constants";
import { usePassportStore } from "src/stores/Passport.store";
import { eventDispatcher } from "src/helpers/eventEmitter";
import { DocumentOpen } from "@asphalt-react/iconpack";
import Icontuner from "@asphalt-react/icontuner";
import { ReactSVG } from "react-svg";
import { PassportAssets } from "src/assets";
import { PaymentMenthodsVirtualAccountDrawer } from "./PaymentMethods.virtualaccount.drawer";
import { PaymentMethodsDetailServiceFeeDrawer } from "./PaymentMethods.detailservicefee.drawer";
import { usePaymentMethodsDrawerState } from "./PaymentMethods.drawer.state";

const BasicDescriptionEl = styled.ul`
  list-style: disc;

  ${(p) => css`
    margin-bottom: ${p.theme.dimension.spacing.xl}px;
    padding: 0 ${p.theme.dimension.spacing.l}px;
  `}
`;

const BasicDescriptionListEl = styled.li`
  ${(p) => css`
    margin-bottom: ${p.theme.dimension.spacing.s}px;

    > p {
      line-height: ${p.theme.text.lineHeight.normal};
    }
  `}
`;

const CardContainerEl = styled(Box)`
  margin-bottom: 32px;

  ${MediaQueries.screen.medium.minWidth} {
    display: flex;
    margin-bottom: 48px;
  }
`;

const CardDescriptionEl = styled(Text)`
  padding-bottom: ${(p) => p.theme.layout.margin.xl}px;

  ${MediaQueries.screen.medium.minWidth} {
    padding-bottom: ${(p) => p.theme.layout.margin.s}px;
  }
`;

const CardEl = styled.div<{ selected?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: ${(p) => p.theme.layout.margin.s}px;
  margin-bottom: ${(p) => p.theme.layout.margin.l}px;
  border: 1px solid
    ${(p) =>
      p.selected
        ? p.theme.colors.borders.brand
        : p.theme.colors.background.disabled};
  border-radius: ${(p) => p.theme.layout.margin.m}px;
  padding: ${(p) => p.theme.layout.margin.xl}px
    ${(p) => p.theme.layout.margin.l}px;
  transition: border 150ms ease-in-out;

  ${MediaQueries.screen.medium.minWidth} {
    width: 50%;
    padding: ${(p) => p.theme.layout.margin.xl}px;

    :first-child {
      margin-right: ${(p) => p.theme.layout.margin.xl}px;
    }

    :hover {
      cursor: pointer;
    }
  }
`;

const CardTitleEl = styled(Text)`
  font-size: ${(p) => p.theme.text.size.large};

  ${MediaQueries.screen.medium.minWidth} {
    font-size: ${(p) => p.theme.text.size.mlarge};
  }
`;

const RadioContainerEl = styled(Box)`
  label div {
    margin-right: 0;
  }
`;

const VirtualAccountTextEl = styled.a`
  text-decoration: underline;

  ${(p) => css`
    color: ${p.theme.colors.text.primary};

    :focus,
    :hover {
      color: ${p.theme.colors.text.primary};
    }
  `}
`;

const BoxDetailServiceFeeEl = styled(Box)`
  margin-top: 36px;
`;

const PaymentLogoEl = styled(Box)`
  display: flex;
  margin: 24px 0 16px 0;
  flex-wrap: wrap;
  gap: 16px;

  svg {
    height: 24px;
  }
`;

export const PaymentMethodCards = observer(() => {
  const passportStore = usePassportStore();
  const { selectedPaymentMethod } = passportStore;
  const translate = useTranslation() as any;
  const {
    showDetailServiceFeeDrawer,
    setShowDetailServiceFeeDrawer,
    showVirtualAccountDrawer,
    setShowVirtualAccountDrawer,
  } = usePaymentMethodsDrawerState();

  useEffect(() => {
    eventDispatcher.publish({ action: "ob_payment_opened" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (value: any) => {
    passportStore.selectedPaymentMethod = value;
  };

  const basicCheckboxRef = useRef<HTMLInputElement>(null);
  const cardCheckboxRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <CardContainerEl>
        <RadioGroup onChange={onChange} defaultValue={selectedPaymentMethod}>
          <CardEl
            selected={selectedPaymentMethod === "basic"}
            onClick={() =>
              basicCheckboxRef.current && basicCheckboxRef.current.click()
            }
          >
            <Box>
              <RadioContainerEl
                marginBottomXl
                flex
                alignItemsCenter
                justifyBetween
              >
                <CardTitleEl fontWeight="demi">
                  {translate("payment_methods.basic_package.title")}
                </CardTitleEl>
                <Radio
                  value="basic"
                  checkbox
                  radius={24}
                  innerRef={basicCheckboxRef}
                />
              </RadioContainerEl>
              <Box>
                <Text marginBottomS>
                  {translate("payment_methods.basic_package.description.label")}
                </Text>
                <BasicDescriptionEl>
                  {translate(
                    "payment_methods.basic_package.description.list"
                  ).map((content: string | string[], index: number) => (
                    <BasicDescriptionListEl key={index}>
                      <Text>
                        {Array.isArray(content)
                          ? content.map((text: string, index: number) => {
                              if (index === 0) {
                                return (
                                  <VirtualAccountTextEl
                                    onClick={() =>
                                      setShowVirtualAccountDrawer(true)
                                    }
                                  >
                                    {text}
                                  </VirtualAccountTextEl>
                                );
                              }
                              return text;
                            })
                          : content}
                      </Text>
                    </BasicDescriptionListEl>
                  ))}
                </BasicDescriptionEl>
                <PaymentLogoEl>
                  <ReactSVG src={PassportAssets.logo.Gopay} />
                  <ReactSVG src={PassportAssets.logo.Mandiri} />
                  <ReactSVG src={PassportAssets.logo.Permata} />
                  <ReactSVG src={PassportAssets.logo.Danamon} />
                  <ReactSVG src={PassportAssets.logo.Bni} />
                  <ReactSVG src={PassportAssets.logo.Bri} />
                  <ReactSVG src={PassportAssets.logo.Cimb} />
                  <ReactSVG src={PassportAssets.logo.BSI} />
                  <ReactSVG src={PassportAssets.logo.SeaBank} />
                  <ReactSVG src={PassportAssets.logo.Qris} />
                </PaymentLogoEl>
                <Tag>
                  <Text fontSize="medium">
                    {translate("payment_methods.basic_package.notes")}
                  </Text>
                </Tag>
              </Box>
            </Box>
            <BoxDetailServiceFeeEl
              flex
              onClick={() => setShowDetailServiceFeeDrawer(true)}
            >
              <Text
                marginRightM
                color="primary"
                fontWeight="bold"
                fontSize="medium"
              >
                {translate(
                  "payment_methods.basic_package.detail_service_fee.label"
                )}
              </Text>
              <Icontuner info>
                <DocumentOpen />
              </Icontuner>
            </BoxDetailServiceFeeEl>
          </CardEl>

          <CardEl
            selected={selectedPaymentMethod === "card"}
            onClick={() =>
              cardCheckboxRef.current && cardCheckboxRef.current.click()
            }
          >
            <Box>
              <RadioContainerEl
                marginBottomXl
                flex
                alignItemsCenter
                justifyBetween
              >
                <CardTitleEl fontWeight="demi">
                  {translate("payment_methods.card_payment.title")}
                </CardTitleEl>
                <Radio
                  value="card"
                  checkbox
                  radius={24}
                  innerRef={cardCheckboxRef}
                />
              </RadioContainerEl>
              {translate("payment_methods.card_payment.description").map(
                (text: string, index: number) => (
                  <CardDescriptionEl key={index}>{text}</CardDescriptionEl>
                )
              )}
              <PaymentLogoEl>
                <ReactSVG src={PassportAssets.logo.Visa} />
                <ReactSVG src={PassportAssets.logo.Mastercard} />
                <ReactSVG src={PassportAssets.logo.Jcb} />
              </PaymentLogoEl>
              <Tag>
                <Text fontSize="medium">
                  {translate("payment_methods.card_payment.notes")}
                </Text>
              </Tag>
            </Box>
            <BoxDetailServiceFeeEl
              flex
              marginTopXl
              onClick={() => setShowDetailServiceFeeDrawer(true)}
            >
              <Text
                marginRightM
                color="primary"
                fontWeight="bold"
                fontSize="medium"
              >
                {translate(
                  "payment_methods.basic_package.detail_service_fee.label"
                )}
              </Text>
              <Icontuner info>
                <DocumentOpen />
              </Icontuner>
            </BoxDetailServiceFeeEl>
          </CardEl>
        </RadioGroup>

        <PaymentMethodsDetailServiceFeeDrawer
          showDrawer={showDetailServiceFeeDrawer}
          setShowDrawer={setShowDetailServiceFeeDrawer}
        />
        <PaymentMenthodsVirtualAccountDrawer
          showDrawer={showVirtualAccountDrawer}
          setShowDrawer={setShowVirtualAccountDrawer}
        />
      </CardContainerEl>
    </>
  );
});
